import React from 'react';
import { Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudFog, CloudLightning, CloudSnow, Snowflake, Wind, Droplets, Thermometer, Sunrise, Sunset } from 'lucide-react';

const ICON_MAP = {
  'sun': Sun,
  'cloud-sun': CloudSun,
  'cloud': Cloud,
  'cloud-fog': CloudFog,
  'cloud-drizzle': CloudDrizzle,
  'cloud-rain': CloudRain,
  'cloud-rain-wind': CloudRain,
  'cloud-lightning': CloudLightning,
  'snowflake': Snowflake,
};

const WEEKDAY = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function WeatherCard({ data }) {
  if (!data) return null;
  const Icon = ICON_MAP[data.icon] || Cloud;
  const updated = new Date(data.updated_at);
  const timeStr = updated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <div data-testid="weather-card" className="relative overflow-hidden rounded-2xl border border-violet-500/40 bg-gradient-to-br from-violet-700/40 via-slate-900 to-cyan-700/30 p-4 sm:p-5 my-2 max-w-md">
      <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-violet-300 font-bold">Live Weather</div>
          <div className="font-display font-black text-base truncate">{data.location}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Updated {timeStr}</div>
        </div>
        <Icon className="w-10 h-10 text-amber-300 drop-shadow-lg flex-shrink-0" />
      </div>

      <div className="relative mt-3 flex items-end gap-3">
        <div className="font-display font-black text-5xl leading-none">{Math.round(data.temperature)}<span className="text-xl text-slate-400">°F</span></div>
        <div className="pb-1">
          <div className="text-sm font-semibold">{data.condition}</div>
          <div className="text-xs text-slate-400">Feels like {Math.round(data.feels_like)}°F</div>
        </div>
      </div>

      <div className="relative mt-3 grid grid-cols-3 gap-2 text-[11px]">
        <div className="flex items-center gap-1 text-slate-300"><Thermometer className="w-3 h-3 text-rose-300" /> {Math.round(data.today.high)}° / {Math.round(data.today.low)}°</div>
        <div className="flex items-center gap-1 text-slate-300"><Droplets className="w-3 h-3 text-cyan-300" /> {data.humidity}%</div>
        <div className="flex items-center gap-1 text-slate-300"><Wind className="w-3 h-3 text-slate-300" /> {Math.round(data.wind_mph)} mph</div>
        {data.today.rain_prob != null && (
          <div className="flex items-center gap-1 text-slate-300"><CloudRain className="w-3 h-3 text-blue-300" /> {data.today.rain_prob}% rain</div>
        )}
        {data.today.sunrise && (
          <div className="flex items-center gap-1 text-slate-300"><Sunrise className="w-3 h-3 text-amber-300" /> {new Date(data.today.sunrise).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
        )}
        {data.today.sunset && (
          <div className="flex items-center gap-1 text-slate-300"><Sunset className="w-3 h-3 text-orange-300" /> {new Date(data.today.sunset).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
        )}
      </div>

      {Array.isArray(data.forecast) && data.forecast.length > 1 && (
        <div className="relative mt-3 pt-3 border-t border-white/10 grid grid-cols-5 gap-1 text-center">
          {data.forecast.map((f, i) => {
            const d = f.date ? new Date(f.date) : null;
            const DIcon = ICON_MAP[f.icon] || Cloud;
            return (
              <div key={i} className="min-w-0">
                <div className="text-[10px] text-slate-400">{d ? WEEKDAY[d.getDay()] : '--'}</div>
                <DIcon className="w-4 h-4 text-violet-200 mx-auto mt-1" />
                <div className="text-[11px] font-bold mt-0.5">{Math.round(f.high)}°</div>
                <div className="text-[10px] text-slate-500">{Math.round(f.low)}°</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
